import type { PropsWithChildren } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useNavigate } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import { useBannerContext } from '@xing-com/crate-jobs-components-banner';
import type { ApplicationType } from '@xing-com/crate-jobs-helpers';
import {
  useGetJobApplicationType,
  useGetLazyNeffiScore,
} from '@xing-com/crate-jobs-hooks';
import { IconButterflyMono } from '@xing-com/icons';

import { useRedirectToLoginWithJobDestUrl } from './use-redirect-to-login-with-job-dest-url';
import type { ApplyTrackingProps } from './use-track-apply-intention';
import { useTrackApplyIntention } from './use-track-apply-intention';

const QUICK_APPLY_NEFFI_SCORE_THRESHOLD = 80;

type InstantApplyButtonState = {
  icon: typeof IconButterflyMono;
  to: undefined;
  onClick: (trackingProps?: ApplyTrackingProps) => void;
  loading: boolean;
};

type ApplyButtonStateProviderProps = {
  applicationType?: ApplicationType;
  jobId: string;
  jobSlug: string;
};

const ApplyButtonStateContext = createContext<InstantApplyButtonState>({
  icon: IconButterflyMono,
  to: undefined,
  onClick: () => ({}),
  loading: false,
});

export const ApplyButtonStateProvider: React.FC<
  PropsWithChildren<ApplyButtonStateProviderProps>
> = ({ applicationType = 'JobXingApplication', children, jobId, jobSlug }) => {
  const isXingApplyFlow = applicationType === 'JobXingApplication';
  const { isLoggedIn } = useLoginState();
  const navigate = useNavigate();
  const { getJobApplicationType, isLoading, data, error } =
    useGetJobApplicationType();
  const { getLazyNeffiScore, normalizedNeffiScore } = useGetLazyNeffiScore();
  const { showErrorBanner } = useBannerContext();
  const redirectToLogin = useRedirectToLoginWithJobDestUrl();
  const { getSearchParameter } = useSearchParameter();
  const trackApplyIntention = useTrackApplyIntention({
    applicationType,
    jobId,
  });
  const [isApplyClickInProgress, setIsApplyClickInProgress] = useState(false);
  const [isLoadingNeffi, setIsLoadingNeffi] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const resetAndShowError = useCallback(
    (errorCopy: string) => {
      showErrorBanner(errorCopy);
      setIsLoadingNeffi(false);
      setIsApplyClickInProgress(false);
    },
    [showErrorBanner]
  );

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (isApplyClickInProgress) {
      if (error) {
        resetAndShowError('JOBS_ERROR_SOMETHING_WENT_WRONG');
        return;
      }
      const {
        isVisibleJob,
        applicationType: apiApplicationType,
        isLoApplyEnabled,
      } = data;

      if (!isVisibleJob) {
        resetAndShowError('JOBS_JOB_DETAILS_XING_APPLY_DEACTIVATED_ERROR');
        return;
      }
      if (applicationType !== apiApplicationType) {
        resetAndShowError('JOBS_JOB_DETAILS_XING_APPLY_TYPE_ERROR');
        return;
      }

      if (isLoadingNeffi || isRedirecting) {
        return;
      }

      setIsRedirecting(true);

      const fixedParams = new URLSearchParams();
      const ijt = getSearchParameter('ijt');
      if (ijt) {
        fixedParams.set('ijt', ijt);
      }
      const applyPath = `${isXingApplyFlow ? '/jobs/apply' : '/jobs/custom-apply'}/${jobSlug}?${fixedParams.toString()}`;

      if (isLoggedIn) {
        if (
          isXingApplyFlow &&
          normalizedNeffiScore >= QUICK_APPLY_NEFFI_SCORE_THRESHOLD
        ) {
          navigate(`/jobs/quick-apply/${jobSlug}?${fixedParams.toString()}`);
        } else {
          navigate(applyPath);
        }
      } else if (isLoApplyEnabled) {
        navigate(applyPath);
      } else {
        redirectToLogin();
      }
    }
  }, [
    data,
    error,
    isLoggedIn,
    isLoading,
    isRedirecting,
    navigate,
    getSearchParameter,
    jobSlug,
    isLoadingNeffi,
    normalizedNeffiScore,
    redirectToLogin,
    isApplyClickInProgress,
    resetAndShowError,
    applicationType,
    isXingApplyFlow,
  ]);

  const onClick = useCallback(
    (trackingProps?: Record<string, unknown>) => {
      trackApplyIntention(trackingProps);
      setIsApplyClickInProgress(true);
      getJobApplicationType({
        variables: { slug: jobSlug },
      });

      if (isLoggedIn && isXingApplyFlow) {
        // onCompleted is not launched on cached lazy queries, see https://github.com/apollographql/apollo-client/issues/9338
        getLazyNeffiScore().then(
          () => setIsLoadingNeffi(false),
          () => setIsLoadingNeffi(false)
        );

        setIsLoadingNeffi(true);
      }
    },
    [
      getJobApplicationType,
      getLazyNeffiScore,
      isLoggedIn,
      isXingApplyFlow,
      jobSlug,
      trackApplyIntention,
    ]
  );

  const applyButtonState = useMemo(() => {
    return {
      icon: IconButterflyMono,
      to: undefined, // overwrites default LO fencing redirection
      onClick,
      loading: isLoading || isLoadingNeffi || isRedirecting,
    };
  }, [isLoading, isLoadingNeffi, isRedirecting, onClick]);

  return (
    <ApplyButtonStateContext.Provider value={applyButtonState}>
      {children}
    </ApplyButtonStateContext.Provider>
  );
};

export const useApplyButtonStateProps = (): InstantApplyButtonState => {
  return useContext(ApplyButtonStateContext);
};
